import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, FormControl, FormLabel, MenuItem, TextField } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useMeetingHubContext } from '../../MeetingHubContext';
import { useMeetingStaffContext } from '../MeetingStaffContext';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import MotionVotingControls from '../MotionVotingControls';
import ConfirmationModal from '../ConfirmationModal';
import { Motion } from 'store/types/Meeting';
import { ConfirmationType, ItemType } from 'store/enums/BallotReviewTypes';
import { defaultTinyMCEProps } from 'util/form';
import {
  createImagesUploadHandler,
  excludeAttendee,
  getAttendeeOptions,
  useSafeAttendeeDisplayValue,
  useSafeSelectIdValue,
} from 'util/meeting';

import commonStyles from 'styles/common.module.scss';

const getMotionOptions = (motions: Motion[]) =>
  motions.map((m) => (
    <MenuItem key={m.id} value={m.id}>
      {m.title}
    </MenuItem>
  ));

const idLabel = 'motion';

const MotionSetupPanel: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { motions, setCurrentMotion, saveMotion, deleteMotion } = useMeetingStaffContext();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMotionId, setSelectedMotionId] = useState('');
  const [draftMotionId, setDraftMotionId] = useState(() => `${idLabel}-${Date.now()}`);
  const [title, setTitle] = useState('');
  const [statement, setStatement] = useState('');
  const [by, setBy] = useState('');
  const [second, setSecond] = useState('');
  const [discussion, setDiscussion] = useState('');

  const sessionMotions = useMemo(() => motions?.filter((m) => !m.documentId), [motions]);

  const motionOptions = useMemo(() => getMotionOptions(sessionMotions), [sessionMotions]);
  const safeSelectedMotionId = useSafeSelectIdValue(selectedMotionId, sessionMotions);

  const byAttendees = useMemo(() => excludeAttendee(attendees || [], second), [attendees, second]);
  const secondAttendees = useMemo(() => excludeAttendee(attendees || [], by), [attendees, by]);

  const byAttendeeOptions = useMemo(
    () => getAttendeeOptions(byAttendees || [], meetingSession?.presidingChairId),
    [byAttendees, meetingSession?.presidingChairId]
  );
  const secondAttendeeOptions = useMemo(
    () => getAttendeeOptions(secondAttendees || [], meetingSession?.presidingChairId),
    [secondAttendees, meetingSession?.presidingChairId]
  );

  const safeBy = useSafeAttendeeDisplayValue(by, byAttendees);
  const safeSecond = useSafeAttendeeDisplayValue(second, secondAttendees);

  const currentMotion: Motion = useMemo(() => {
    return {
      id: selectedMotionId || draftMotionId,
      title,
      statement,
      by: safeBy,
      second: safeSecond,
      discussion,
    };
  }, [discussion, draftMotionId, safeBy, safeSecond, selectedMotionId, statement, title]);

  const handleMotionChange = useCallback((event) => {
    const motionId = event.target.value;

    setSelectedMotionId(motionId);
    if (!motionId) {
      setDraftMotionId(`${idLabel}-${Date.now()}`);
    }
  }, []);

  const makeSaveMotionHandler = useCallback(
    (notify?: boolean) => async (): Promise<Motion | null> => {
      const saved = await saveMotion?.();

      if (saved) {
        if (notify) {
          enqueueSnackbar(`Motion saved`, { variant: 'success' });
        }
        setSelectedMotionId(saved.id);
        setDraftMotionId(`${idLabel}-${Date.now()}`);
        return saved;
      }

      return null;
    },
    [enqueueSnackbar, saveMotion]
  );

  const handleSaveMotion = useMemo(() => makeSaveMotionHandler(true), [makeSaveMotionHandler]);
  const handleSaveMotionNoNotify = useMemo(() => makeSaveMotionHandler(false), [makeSaveMotionHandler]);

  const handleDeleteMotion = useCallback(() => {
    if (!selectedMotionId) {
      enqueueSnackbar(`No motion selected to delete`, { variant: 'warning' });
      return;
    }

    deleteMotion?.(selectedMotionId);
  }, [selectedMotionId, deleteMotion, enqueueSnackbar]);

  const handleClearMotion = useCallback(() => {
    setSelectedMotionId('');
    setDraftMotionId(`${idLabel}-${Date.now()}`);
    setTitle('');
    setStatement('');
    setBy('');
    setSecond('');
    setDiscussion('');
  }, []);

  const handleSaveClick = useCallback(() => {
    if (selectedMotionId) {
      setSaveModalOpen(true);
      return;
    }
    handleSaveMotion();
  }, [handleSaveMotion, selectedMotionId]);

  const handleSaveModalSubmit = useCallback(() => {
    setSaveModalOpen(false);
    handleSaveMotion();
  }, [handleSaveMotion]);

  const handleDeleteClick = useCallback(() => {
    if (selectedMotionId) {
      setDeleteModalOpen(true);
      return;
    }
    handleDeleteMotion();
  }, [handleDeleteMotion, selectedMotionId]);

  const handleDeleteModalSubmit = useCallback(() => {
    setDeleteModalOpen(false);
    handleDeleteMotion();
  }, [handleDeleteMotion]);

  useEffect(() => {
    if (!sessionMotions?.length) return;

    const motion = sessionMotions.find((m) => m.id === selectedMotionId);

    setTitle(motion?.title || '');
    setStatement(motion?.statement || '');
    setBy(motion?.by || '');
    setSecond(motion?.second || '');
    setDiscussion(motion?.discussion || '');
  }, [sessionMotions, selectedMotionId]);

  useEffect(() => {
    setCurrentMotion?.(currentMotion);
  }, [currentMotion, setCurrentMotion]);

  return (
    <>
      <GridContainer>
        <GridItem md={6}>
          <TextField label={'Load Motion'} select value={safeSelectedMotionId} onChange={handleMotionChange}>
            <MenuItem value={''}>-- New Motion --</MenuItem>
            {motionOptions}
          </TextField>
        </GridItem>
        <GridItem md={6} className={commonStyles.buttonGroup}>
          <Button variant="contained" color="primary" onClick={handleSaveClick}>
            Save
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteClick}>
            Delete
          </Button>
        </GridItem>
        <GridItem>
          <TextField
            label={'Title'}
            value={title}
            required
            onChange={(event) => {
              setTitle(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem>
          <TextField
            label={'Motion'}
            value={statement}
            required
            onChange={(event) => {
              setStatement(event.target.value);
            }}
          ></TextField>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'By'}
            select
            required
            value={safeBy}
            onChange={(event) => {
              setBy(event.target.value);
            }}
          >
            <MenuItem value={''}>-- None --</MenuItem>
            {byAttendeeOptions}
          </TextField>
        </GridItem>
        <GridItem md={6}>
          <TextField
            label={'Second'}
            select
            required
            value={safeSecond}
            onChange={(event) => {
              setSecond(event.target.value);
            }}
          >
            <MenuItem value={''}>-- None --</MenuItem>
            {secondAttendeeOptions}
          </TextField>
        </GridItem>
        <GridItem>
          <FormControl className="w-100">
            <FormLabel>Discussion</FormLabel>
            <Editor
              {...defaultTinyMCEProps}
              id="motion-discussion-editor"
              value={discussion}
              onEditorChange={(newValue: string) => setDiscussion(newValue)}
              init={{
                ...defaultTinyMCEProps.init,
                images_upload_handler: createImagesUploadHandler(meetingId),
              }}
            />
          </FormControl>
        </GridItem>
        <GridItem>
          <MotionVotingControls
            currentMotion={currentMotion}
            saveMotion={handleSaveMotionNoNotify}
            onClearMotion={handleClearMotion}
          />
        </GridItem>
      </GridContainer>
      <ConfirmationModal
        itemType={ItemType.Motion}
        confirmationType={ConfirmationType.Overwrite}
        open={saveModalOpen}
        onSubmit={handleSaveModalSubmit}
        onClose={() => setSaveModalOpen(false)}
      />
      <ConfirmationModal
        itemType={ItemType.Motion}
        confirmationType={ConfirmationType.Delete}
        open={deleteModalOpen}
        onSubmit={handleDeleteModalSubmit}
        onClose={() => setDeleteModalOpen(false)}
      />
    </>
  );
};

export default MotionSetupPanel;
